import {
  SpsAnlagenteilOptionen,
  SpsFortschritt,
  SpsImportEintrag,
  SpsListenFilter,
  SpsPruefstatus,
  SpsSeite,
  SpsSignal,
  SpsSignalFilter,
  SpsSignalNachbarn,
  leererSpsFortschritt
} from '../../Models/Sps';
import { SpsOfflineSignal } from '../../Models/offline.model';

/*
 * Lokale Fassung der serverseitigen Filter, Sortierung und Kennzahlen der
 * SPS-Prüfung und der SPS-Listen (SpsSignalService.Gefiltert, Sortiere,
 * FiltereEintraege, SortiereEintraege, FortschrittAsync, Anlagenteil-
 * Optionen).
 *
 * Reine Funktionen ohne Angular-Abhängigkeit, damit sie sich einzeln prüfen
 * lassen. Die Regeln spiegeln die SQL-Fassung bewusst Zeile für Zeile:
 * weicht die Offline-Ansicht von der Online-Ansicht ab, zweifelt der Prüfer
 * an den Daten – und damit an der ganzen Funktion.
 *
 * Textvergleiche sind wie SQL Server LIKE: ohne Groß-/Kleinschreibung.
 */

/** Reihenfolge der Status, wie das Enum im Backend (Sortierung „status"). */
const STATUS_REIHENFOLGE: SpsPruefstatus[] = ['Ungeprueft', 'Ok', 'NichtOk', 'Reserve'];

function enthaelt(wert: string | null | undefined, suche: string): boolean {
  return !!wert && wert.toLowerCase().includes(suche);
}

function suchtext(wert: string | null | undefined): string | null {
  const t = (wert ?? '').trim();
  return t ? t.toLowerCase() : null;
}

/** Wie SQL: NULL sortiert vor allem anderen; sonst Zeichenketten ohne Groß-/Kleinschreibung. */
function vergleicheText(a: string | null | undefined, b: string | null | undefined): number {
  if (a == null && b == null) { return 0; }
  if (a == null) { return -1; }
  if (b == null) { return 1; }
  return a.localeCompare(b, 'de', { sensitivity: 'base' });
}

function vergleicheZahl(a: number | null | undefined, b: number | null | undefined): number {
  if (a == null && b == null) { return 0; }
  if (a == null) { return -1; }
  if (b == null) { return 1; }
  return a - b;
}

function statusRang(status: SpsPruefstatus | null | undefined): number | null {
  if (!status) { return null; }
  const i = STATUS_REIHENFOLGE.indexOf(status);
  return i < 0 ? null : i;
}

// ─────────────────────────────────────────────────────────────────────────
// SIGNALE (SPS-Prüfung)
// ─────────────────────────────────────────────────────────────────────────

/** Die gefilterte (unsortierte) Signalmenge – wie `SpsSignalService.Gefiltert`. */
export function filtereSignale(signale: SpsOfflineSignal[], filter: SpsSignalFilter): SpsOfflineSignal[] {
  const name = suchtext(filter.name);
  const adresse = suchtext(filter.adresse?.replace(/^%+/, ''));
  const bmk = suchtext(filter.bmk);
  const beschreibung = suchtext(filter.beschreibung);
  const station = suchtext(filter.station);
  const suche = suchtext(filter.suche?.replace(/^%+/, ''));

  return signale.filter(s => {
    // Prüfbar = mit mindestens einer Quelle.
    if ((s.quellen?.length ?? 0) === 0) { return false; }

    if (filter.richtung && s.richtung !== filter.richtung) { return false; }
    if (filter.status && s.pruefstatus !== filter.status) { return false; }
    if (filter.nurOffen && s.pruefstatus !== 'Ungeprueft' && s.pruefstatus !== 'NichtOk') { return false; }
    if (filter.nurUngeklaert && !s.quellen.some(q => q.status === 'Ungeklaert')) { return false; }
    if (filter.nurGeaendertSeitPruefung && !s.geaendertSeitPruefungAmUtc) { return false; }

    if (filter.ohneAnlagenteil) {
      if (s.quellen.some(q => q.eintrag?.anlagenteilId != null)) { return false; }
    } else if (filter.anlagenteilId != null) {
      if (!s.quellen.some(q => q.status === 'Bestaetigt' && q.eintrag?.anlagenteilId === filter.anlagenteilId)) {
        return false;
      }
    }

    if (name && !enthaelt(s.name, name)) { return false; }

    if (adresse
      && !enthaelt(s.adresseNormiert, adresse)
      && !s.quellen.some(q => enthaelt(q.eintrag?.adresseOriginal, adresse))) {
      return false;
    }

    if (bmk && !s.quellen.some(q => enthaelt(q.eintrag?.bmk, bmk))) { return false; }
    if (beschreibung && !enthaelt(s.beschreibung, beschreibung)) { return false; }

    if (station
      && !enthaelt(s.stationsSchluessel, station)
      && !s.quellen.some(q => enthaelt(q.eintrag?.ort, station) || enthaelt(q.eintrag?.stationsName, station))) {
      return false;
    }

    if (suche
      && !enthaelt(s.name, suche)
      && !enthaelt(s.adresseNormiert, suche)
      && !enthaelt(s.beschreibung, suche)
      && !enthaelt(s.stationsSchluessel, suche)
      && !s.quellen.some(q =>
        enthaelt(q.eintrag?.bmk, suche)
        || enthaelt(q.eintrag?.adresseOriginal, suche)
        || enthaelt(q.eintrag?.symbol, suche))) {
      return false;
    }

    return true;
  });
}

/** Sortierung wie `SpsSignalService.Sortiere`: Feld, dann Name, dann Id. */
export function sortiereSignale<T extends SpsSignal>(signale: T[], sortierung: string | null | undefined): T[] {
  let s = (sortierung ?? 'adresse').trim().toLowerCase();
  const absteigend = s.endsWith('_desc');

  if (absteigend) {
    s = s.slice(0, -5);
  }

  const primaer = (a: T, b: T): number => {
    switch (s) {
      case 'name': return vergleicheText(a.name, b.name);
      case 'status': return vergleicheZahl(statusRang(a.pruefstatus), statusRang(b.pruefstatus));
      case 'station': return vergleicheText(a.stationsSchluessel, b.stationsSchluessel);
      case 'geprueft': return vergleicheText(a.zuletztGeprueftAmUtc, b.zuletztGeprueftAmUtc);
      default: return vergleicheText(a.adresseNormiert, b.adresseNormiert);
    }
  };

  return [...signale].sort((a, b) => {
    const p = primaer(a, b) * (absteigend ? -1 : 1);
    if (p !== 0) { return p; }

    const n = vergleicheText(a.name, b.name);
    return n !== 0 ? n : a.id - b.id;
  });
}

/** Eine Seite aus einer bereits gefilterten und sortierten Menge – wie `SpsSeiteDto`. */
export function seiteVon<T>(alle: T[], seite: number | undefined, groesse: number | undefined, standardGroesse: number, max = 500): SpsSeite<T> {
  const g = Math.min(Math.max(1, groesse && groesse > 0 ? groesse : standardGroesse), max);
  const s = Math.max(1, seite ?? 1);

  return {
    gesamt: alle.length,
    seite: s,
    groesse: g,
    eintraege: alle.slice((s - 1) * g, s * g)
  };
}

/** Fortschritt wie `SpsSignalService.FortschrittAsync` + `Vervollstaendige`. */
export function fortschrittVon(signale: SpsOfflineSignal[]): SpsFortschritt {
  const f = leererSpsFortschritt();
  const pruefbare = signale.filter(s => (s.quellen?.length ?? 0) > 0);

  for (const s of pruefbare) {
    f.gesamt++;

    switch (s.pruefstatus) {
      case 'Ok': f.ok++; break;
      case 'NichtOk': f.nichtOk++; break;
      case 'Reserve': f.reserve++; break;
      default: f.ungeprueft++; break;
    }

    switch (s.richtung) {
      case 'Eingang': f.eingaenge++; break;
      case 'Ausgang': f.ausgaenge++; break;
      default: f.richtungUnbekannt++; break;
    }

    if (s.quellen.some(q => q.status === 'Ungeklaert')) {
      f.ungeklaert++;
    }
  }

  f.bearbeitet = f.ok + f.nichtOk + f.reserve;
  f.pruefbar = f.gesamt - f.reserve;
  f.bearbeitetProzent = f.gesamt === 0 ? 0 : Math.round(100 * f.bearbeitet / f.gesamt);
  f.erfolgProzent = f.ok + f.nichtOk === 0 ? 0 : Math.round(100 * f.ok / (f.ok + f.nichtOk));

  return f;
}

/** Vorheriges/nächstes Signal in einer gefilterten, sortierten Menge – wie `NachbarnAsync`. */
export function nachbarnVon(sortiert: SpsSignal[], signalId: number): SpsSignalNachbarn {
  const ids = sortiert.map(s => s.id);
  const index = ids.indexOf(signalId);

  return {
    signalId,
    gesamt: ids.length,
    position: index < 0 ? 0 : index + 1,
    vorherigesId: index > 0 ? ids[index - 1] : null,
    naechstesId: index >= 0 && index < ids.length - 1 ? ids[index + 1] : null
  };
}

/** Anlagenteil-Optionen der Prüfung – wie `AnlagenteilOptionenDerPruefungAsync`. */
export function anlagenteilOptionenDerPruefung(signale: SpsOfflineSignal[]): SpsAnlagenteilOptionen {
  const pruefbare = signale.filter(s => (s.quellen?.length ?? 0) > 0);
  const zaehler = new Map<number, { name: string; sortOrder: number; signale: Set<number> }>();
  let ohne = 0;

  for (const s of pruefbare) {
    if (!s.quellen.some(q => q.eintrag?.anlagenteilId != null)) {
      ohne++;
    }

    for (const q of s.quellen) {
      const id = q.eintrag?.anlagenteilId;

      if (q.status !== 'Bestaetigt' || id == null) {
        continue;
      }

      const eintrag = zaehler.get(id) ?? { name: q.eintrag?.anlagenteilName ?? '', sortOrder: 0, signale: new Set<number>() };
      eintrag.signale.add(s.id);
      zaehler.set(id, eintrag);
    }
  }

  return optionen(
    [...zaehler.entries()].map(([id, z]) => ({ anlagenteilId: id, name: z.name, sortOrder: z.sortOrder, anzahl: z.signale.size })),
    ohne,
    pruefbare.length
  );
}

function optionen(
  liste: { anlagenteilId: number; name: string; sortOrder: number; anzahl: number }[],
  ohne: number,
  gesamt: number
): SpsAnlagenteilOptionen {
  return {
    gesamt,
    ohneAnlagenteil: Math.max(0, ohne),
    anlagenteile: liste
      .filter(a => a.anzahl > 0)
      .map(a => ({ ...a, name: a.name.trim() }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }) || a.anlagenteilId - b.anlagenteilId)
  };
}

// ─────────────────────────────────────────────────────────────────────────
// ORIGINALZEILEN (SPS-Listen)
// ─────────────────────────────────────────────────────────────────────────

/** Alle Bedingungen des Listenfilters – wie `FiltereEintraege`. */
export function filtereEintraege(eintraege: SpsImportEintrag[], f: SpsListenFilter): SpsImportEintrag[] {
  const suche = suchtext(f.suche);
  const name = suchtext(f.name);
  const adresse = suchtext(f.adresse);
  const bmk = suchtext(f.bmk);
  const beschreibung = suchtext(f.beschreibung);
  const station = suchtext(f.station);
  const anschluss = suchtext(f.anschluss);
  const kanal = suchtext(f.kanal);
  const ort = suchtext(f.ort);
  const platzierung = suchtext(f.platzierung);
  const datenTyp = suchtext(f.datenTyp);

  return eintraege.filter(e => {
    if (suche
      && !enthaelt(e.signalname, suche) && !enthaelt(e.symbol, suche) && !enthaelt(e.adresseOriginal, suche)
      && !enthaelt(e.bmk, suche) && !enthaelt(e.beschreibung, suche) && !enthaelt(e.stationsName, suche)
      && !enthaelt(e.anschluss, suche) && !enthaelt(e.kanal, suche) && !enthaelt(e.ort, suche)
      && !enthaelt(e.platzierung, suche) && !enthaelt(e.datenTyp, suche)) {
      return false;
    }

    // Richtung und Prüfstatus gehören zum gemeinsamen Signal; eine Zeile
    // ohne Signal gilt als Unbekannt und hat keinen Prüfstatus.
    if (f.richtung) {
      const signalRichtung = e.signalId == null ? 'Unbekannt' : (e.richtung ?? 'Unbekannt');
      if (signalRichtung !== f.richtung) { return false; }
    }

    if (f.status && (e.signalId == null || e.pruefstatus !== f.status)) { return false; }

    if (f.nurOffen && (e.signalId == null || (e.pruefstatus !== 'Ungeprueft' && e.pruefstatus !== 'NichtOk'))) {
      return false;
    }

    if (name && !enthaelt(e.signalname, name)) { return false; }
    if (adresse && !enthaelt(e.adresseOriginal, adresse) && !enthaelt(e.adresseNormiert, adresse)) { return false; }
    if (bmk && !enthaelt(e.bmk, bmk)) { return false; }
    if (beschreibung && !enthaelt(e.beschreibung, beschreibung)) { return false; }
    if (station && !enthaelt(e.stationsName, station)) { return false; }
    if (anschluss && !enthaelt(e.anschluss, anschluss)) { return false; }
    if (kanal && !enthaelt(e.kanal, kanal)) { return false; }
    if (ort && !enthaelt(e.ort, ort)) { return false; }
    if (platzierung && !enthaelt(e.platzierung, platzierung)) { return false; }
    if (datenTyp && !enthaelt(e.datenTyp, datenTyp)) { return false; }

    // Anlagenteil: eigener oder vom Signal übernommener – im DTO bereits aufgelöst.
    if (f.ohneAnlagenteil) {
      if (e.anlagenteilId != null) { return false; }
    } else if (f.anlagenteilId != null) {
      if (e.anlagenteilId !== f.anlagenteilId) { return false; }
    }

    return true;
  });
}

/** Sortierung wie `SortiereEintraege`: Feld, dann Excel-Zeile, dann Id. */
export function sortiereEintraege(eintraege: SpsImportEintrag[], sortierung: string | null | undefined): SpsImportEintrag[] {
  let s = (sortierung ?? 'zeile').trim().toLowerCase();
  const absteigend = s.endsWith('_desc');

  if (absteigend) {
    s = s.slice(0, -5);
  }

  const primaer = (a: SpsImportEintrag, b: SpsImportEintrag): number => {
    switch (s) {
      case 'adresse': return vergleicheText(a.adresseOriginal, b.adresseOriginal);
      case 'name': return vergleicheText(a.signalname, b.signalname);
      case 'station': return vergleicheText(a.stationsName, b.stationsName);
      case 'status': return vergleicheZahl(statusRang(a.pruefstatus), statusRang(b.pruefstatus));
      default: return a.excelZeile - b.excelZeile;
    }
  };

  return [...eintraege].sort((a, b) => {
    const p = primaer(a, b) * (absteigend ? -1 : 1);
    if (p !== 0) { return p; }

    return (a.excelZeile - b.excelZeile) || (a.id - b.id);
  });
}

/** Anlagenteil-Optionen einer Liste – wie `AnlagenteilOptionenDerListeAsync` (eigener oder übernommener Anlagenteil). */
export function anlagenteilOptionenDerListe(eintraege: SpsImportEintrag[]): SpsAnlagenteilOptionen {
  const zaehler = new Map<number, { name: string; anzahl: number }>();
  let ohne = 0;

  for (const e of eintraege) {
    if (e.anlagenteilId == null) {
      ohne++;
      continue;
    }

    const z = zaehler.get(e.anlagenteilId) ?? { name: e.anlagenteilName ?? '', anzahl: 0 };
    z.anzahl++;
    zaehler.set(e.anlagenteilId, z);
  }

  return optionen(
    [...zaehler.entries()].map(([id, z]) => ({ anlagenteilId: id, name: z.name, sortOrder: 0, anzahl: z.anzahl })),
    ohne,
    eintraege.length
  );
}
